import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Paper,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { API_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const DocumentUpload = ({ onUploadSuccess }) => {
  const { user, tokenManager } = useAuth();
  const [formData, setFormData] = useState({
    module: '',
    subModule: '',
    documentName: '',
    description: '',
    isTrackable: false,
    requiredReviewTime: '', // Time in mm:ss format
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingName, setCheckingName] = useState(false);

  // Fixed modules - these are predefined
  const availableModules = ['Induction', 'Training'];

  // Helper function to convert mm:ss to total seconds
  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    const parts = timeString.split(':');
    if (parts.length === 1) {
      // Just minutes
      return parseInt(parts[0]) * 60 || 0;
    } else if (parts.length === 2) {
      // mm:ss format
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      // Validate seconds (must be 0-59)
      if (seconds > 59) {
        throw new Error('Seconds must be between 0 and 59');
      }
      return minutes * 60 + seconds;
    }
    return 0;
  };

  // Helper function to convert seconds to mm:ss
  const formatSecondsToTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to validate mm:ss format
  const isValidTimeFormat = (timeString) => {
    if (!timeString) return true; // Empty is valid (optional field)
    const regex = /^(\d{1,3}):([0-5]\d)$|^\d{1,3}$/;
    return regex.test(timeString);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'isTrackable' ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (name === 'documentName') {
      setError('');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (300MB limit)
      const maxSize = 300 * 1024 * 1024; // 300MB in bytes
      if (selectedFile.size > maxSize) {
        setError('File size too large. Maximum file size is 300MB.');
        setFile(null);
        return;
      }

      // Check file type - only allow PDF and video files
      const allowedTypes = [
        'application/pdf',
        'video/mp4',
        'video/avi',
        'video/mov',
        'video/wmv',
        'video/webm'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Only PDF and video files are allowed.');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError('');
    }
  };

  // Check if document name already exists
  const checkDocumentNameExists = async (documentName) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', formData.module);
      queryParams.append('subModule', formData.subModule);

      const response = await fetch(`${API_URL}/documents?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const existingDoc = result.documents.find(
          doc => doc.documentName.toLowerCase() === documentName.toLowerCase()
        );
        return existingDoc;
      }
      return null;
    } catch (error) {
      console.error('Error checking document name:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!formData.module) {
      setError('Please select a module');
      return;
    }

    if (!formData.subModule) {
      setError('Please select a sub module');
      return;
    }

    if (!formData.documentName) {
      setError('Please fill in document name');
      return;
    }

    // Validate time format if time is provided
    if (formData.requiredReviewTime && !isValidTimeFormat(formData.requiredReviewTime)) {
      setError('Invalid time format. Use MM:SS (e.g., 5:30) or just MM (e.g., 5)');
      return;
    }

    // Check for duplicate document name
    setCheckingName(true);
    const existingDoc = await checkDocumentNameExists(formData.documentName);
    setCheckingName(false);

    if (existingDoc) {
      setError(`A document with the name "${formData.documentName}" already exists in ${formData.module} > ${formData.subModule}. Please choose a different name.`);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('module', formData.module);
    uploadData.append('subModule', formData.subModule);
    uploadData.append('documentName', formData.documentName);
    uploadData.append('description', formData.description || ''); // Optional description
    
    // Parse time string to seconds
    let timeInSeconds = 0;
    try {
      timeInSeconds = parseTimeToSeconds(formData.requiredReviewTime);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    
    // Auto-enable tracking if there's a required review time
    const shouldTrack = formData.isTrackable || (timeInSeconds > 0);
    uploadData.append('isTrackable', shouldTrack.toString());
    uploadData.append('requiredReviewTime', timeInSeconds.toString());
    uploadData.append('uploadedBy', user.email);

    try {
      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          // Don't set Content-Type for FormData - let the browser set it automatically
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess('Document uploaded successfully!');
        setFormData({
          module: '',
          subModule: '',
          documentName: '',
          description: '',
          isTrackable: false,
          requiredReviewTime: '',
        });
        setFile(null);
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Upload New Document
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You can upload multiple documents (PDFs, videos, etc.) to any module and submodule. Each document should have a unique name within its module and submodule.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Module</InputLabel>
                <Select
                  name="module"
                  value={formData.module}
                  onChange={handleInputChange}
                  label="Module"
                >
                  {availableModules.map((module) => (
                    <MenuItem key={module} value={module}>
                      {module}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                name="subModule"
                label="Sub Module"
                value={formData.subModule}
                onChange={handleInputChange}
                placeholder="Enter sub module name"
                disabled={!formData.module}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                name="documentName"
                label="Document Name"
                value={formData.documentName}
                onChange={handleInputChange}
                placeholder="Enter document name"
              />
            </Grid>
             <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter document description"
                helperText="Provide a detailed description of the document content and purpose"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="requiredReviewTime"
                label="Required Review Time (mm:ss)"
                value={formData.requiredReviewTime}
                onChange={handleInputChange}
                placeholder="Enter time (e.g., 5:30 for 5 min 30 sec, or just 5 for 5 minutes)"
                helperText="Format: MM:SS or just MM. Example: 5:30 or 5. Set to 0 for no time requirement."
                error={!isValidTimeFormat(formData.requiredReviewTime) && formData.requiredReviewTime !== ''}
              />
            </Grid>


            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  border: '2px dashed',
                  borderColor: file ? 'success.main' : 'grey.300',
                  backgroundColor: file ? 'success.50' : 'grey.50',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  },
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".pdf,.mp4,.avi,.mov,.wmv,.webm"
                />
                <CloudUploadIcon sx={{ fontSize: 48, color: file ? 'success.main' : 'grey.500', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {file ? file.name : 'Click to select file'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: PDF, MP4, AVI, MOV, WMV, WEBM (Max: 300MB)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="isTrackable"
                    checked={formData.isTrackable}
                    onChange={handleInputChange}
                  />
                }
                label="Track document access and downloads"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || checkingName || !file || !formData.module || !formData.subModule}
                startIcon={(loading || checkingName) ? <CircularProgress size={20} /> : null}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                {checkingName ? 'Checking Name...' : loading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentUpload; 